import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useRef, useState } from 'react';
import { useSite } from '../../context/site';
import DateRangeReport from './date-range-select';

function Parents01Report() {
    const { GetParents01Report } = useSite();

    const dt = useRef(null);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dateBegin, setDateBegin] = useState(new Date());
    const [dateEnd, setDateEnd] = useState(new Date());
    const [run, setRun] = useState(true);
    const [countChildren, setCountChildren] = useState(0);
    const [countServicesIndividual, setServicesIndividual] = useState(0);
    const [countServicesGroup, setServicesGroup] = useState(0);

    const exportCSV = (selectionOnly) => {
        dt.current.exportCSV({ selectionOnly });
    };

    const getChildrenNames = (children) => {
        if (!Array.isArray(children)) {
            return [];
        }

        return children.map((c) => {
            return `${c.nameFirst} ${c.nameLast} (Age ${c.age})`;
        });
    };

    const childrenNames = (parent) => {
        if (parent.childrenNames.length === 0) {
            return 'No Children';
        }

        return (
            <ul>
                {parent.childrenNames.map((v, i) => {
                    return <li key={i}>{v}</li>;
                })}
            </ul>
        );
    };

    const getServiceDetails = (services) => {
        if (!Array.isArray(services)) {
            return [];
        }

        const servicesReduced = services.reduce((prev, curr) => {
            if (!prev[curr.serviceType.itemName]) {
                prev[curr.serviceType.itemName] = 1;
            } else {
                prev[curr.serviceType.itemName]++;
            }

            return { ...prev };
        }, {});

        const display = [];

        Object.entries(servicesReduced).forEach(([k, v]) => {
            display.push(`${k} (${v})`);
        });

        return display;
    };

    const serviceDetailsIndividual = (parent) => {
        if (parent.servicesDisplayIndividual.length === 0) {
            return 'No Individual Services';
        }

        return (
            <ul>
                {parent.servicesDisplayIndividual.map((v, i) => {
                    return <li key={i}>{v}</li>;
                })}
            </ul>
        );
    };

    const serviceDetailsGroup = (parent) => {
        if (parent.servicesDisplayGroup.length === 0) {
            return 'No Group Services';
        }

        return (
            <ul>
                {parent.servicesDisplayGroup.map((v, i) => {
                    return <li key={i}>{v}</li>;
                })}
            </ul>
        );
    };

    if (run) {
        return (
            <>
                <DateRangeReport
                    onRunReport={({ start, end }) => {
                        setLoading(true);
                        setDateBegin(new Date(start));
                        setDateEnd(new Date(end));
                        setRun(false);
                        GetParents01Report({ start, end }, (res) => {
                            const parents = res.data.parents.map((p) => {
                                return {
                                    ...p,
                                    dateAdded: new Date(p.dateAdded).toLocaleDateString(),
                                    childrenNames: getChildrenNames(p.children),
                                    servicesDisplayIndividual: getServiceDetails(
                                        p.servicesIndividual
                                    ),
                                    servicesDisplayGroup: getServiceDetails(p.servicesGroup)
                                };
                            });

                            setRows(parents);
                            setCountChildren(
                                parents.reduce((p, c) => {
                                    return p + c.children.length;
                                }, 0)
                            );
                            setServicesIndividual(
                                parents.reduce((p, c) => {
                                    return p + c.servicesIndividual.length;
                                }, 0)
                            );
                            setServicesGroup(
                                parents.reduce((p, c) => {
                                    return p + c.servicesGroup.length;
                                }, 0)
                            );
                            setLoading(false);
                        });
                    }}
                />
            </>
        );
    }

    return (
        <>
            <div className="mt-2">
                <Button label="Change Date" icon="pi pi-check" onClick={() => setRun(true)} />
            </div>

            <div className="mt-2">
                <span>Start Date: {dateBegin.toLocaleDateString()}</span>
            </div>

            <div className="mt-2">
                <span>End Date: {dateEnd.toLocaleDateString()}</span>
            </div>

            <div className="mt-2">
                <span>Number of Parents&nbsp;</span>
                <Badge value={rows.length} severity="success"></Badge>
            </div>

            <div className="mt-2">
                <span>Number of Children&nbsp;</span>
                <Badge value={countChildren} severity="info"></Badge>
            </div>

            <div className="mt-2">
                <span>Number of Individual Services&nbsp;</span>
                <Badge value={countServicesIndividual} severity="danger"></Badge>
            </div>

            <div className="mt-2">
                <span>Number of Group Services&nbsp;</span>
                <Badge value={countServicesGroup} severity="warning"></Badge>
            </div>

            <div className="mt-2">
                <DataTable
                    ref={dt}
                    exportFilename="parents-01"
                    header={
                        <Button
                            type="button"
                            icon="pi pi-file"
                            rounded
                            onClick={() => exportCSV(false)}
                            data-pr-tooltip="CSV"
                        />
                    }
                    value={rows}
                    emptyMessage="No parents found"
                    showGridlines
                    stripedRows
                    removableSort
                    resizableColumns
                    paginator
                    rows={5}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    loading={loading}
                >
                    <Column field="_id" header="Id"></Column>
                    <Column field="dateAdded" header="Date Added"></Column>
                    <Column field="nameFull" header="Name"></Column>
                    <Column field="email" header="Email"></Column>
                    <Column field="streetAddress1" header="Street Address 1"></Column>
                    <Column field="streetAddress2" header="Street Address 2"></Column>
                    <Column field="city" header="Town/City"></Column>
                    <Column field="state.itemName" header="State"></Column>
                    <Column field="zipCode" header="Zip Code"></Column>
                    <Column field="tag" header="TAG"></Column>
                    <Column field="countChildren" header="Children Count"></Column>
                    <Column
                        field="childrenNames"
                        body={childrenNames}
                        header="Children Names"
                    ></Column>
                    <Column
                        field="countServicesIndividual"
                        header="Individual Services Count"
                    ></Column>
                    <Column
                        field="servicesDisplayIndividual"
                        body={serviceDetailsIndividual}
                        header="Individual Services Details"
                    ></Column>
                    <Column field="countServicesGroup" header="Group Services Count"></Column>
                    <Column
                        field="servicesDisplayGroup"
                        body={serviceDetailsGroup}
                        header="Group Services Details"
                    ></Column>
                </DataTable>
            </div>
        </>
    );
}

export default Parents01Report;
